// store/slices/profileSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import { getUserData, setUserData } from "../../utils/authUtils";

// Get User Profile
export const getUserProfile = createAsyncThunk(
  "profile/getUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getProfile();
      if (response.success === true) {
        const userData = response.data?.user || response.data;
        setUserData(userData);
        return userData;
      }
      return rejectWithValue(response.message || "Failed to get profile");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get profile"
      );
    }
  }
);

// Update Profile
export const updateProfile = createAsyncThunk(
  "profile/updateProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await api.updateProfile(profileData);
      if (response.success === true) {
        const userData = response.data?.user || response.data;
        setUserData(userData);
        return userData;
      }
      return rejectWithValue(response.message || "Failed to update profile");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update profile"
      );
    }
  }
);

// Update Bank Details
export const updateBankDetails = createAsyncThunk(
  "profile/updateBankDetails",
  async (bankData, { rejectWithValue }) => {
    try {
      const response = await api.updateBankDetails(bankData);
      if (response.success === true) {
        const bankDetails = response.data;
        const userData = getUserData() || {};
        userData.bank_details = bankDetails;
        setUserData(userData);
        return { userData, bankDetails };
      }
      return rejectWithValue(
        response.message || "Failed to update bank details"
      );
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update bank details"
      );
    }
  }
);

// ============= SLICE =============

const initialState = {
  user: getUserData(),
  loading: false,
  updatingProfile: false,
  updatingBank: false,
  error: null,
  success: null,
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearProfileError: (state) => {
      state.error = null;
    },
    clearProfileSuccess: (state) => {
      state.success = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      if (action.payload) setUserData(action.payload);
    },
    resetProfileState: (state) => {
      state.loading = false;
      state.updatingProfile = false;
      state.updatingBank = false;
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.user = null;
      })
      .addCase(updateProfile.pending, (state) => {
        state.updatingProfile = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.updatingProfile = false;
        state.user = action.payload;
        state.success = "Profile updated successfully";
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.updatingProfile = false;
        state.error = action.payload;
      })
      .addCase(updateBankDetails.pending, (state) => {
        state.updatingBank = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateBankDetails.fulfilled, (state, action) => {
        state.updatingBank = false;
        if (state.user) {
          state.user.bank_details = action.payload.bankDetails;
        }
        state.success = "Bank details updated successfully";
      })
      .addCase(updateBankDetails.rejected, (state, action) => {
        state.updatingBank = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearProfileError,
  clearProfileSuccess,
  setUser,
  resetProfileState,
} = profileSlice.actions;

export default profileSlice.reducer;